import { Link } from "@tanstack/react-router";
import { CloudIcon, LaptopIcon, PauseIcon, PlayIcon, PlusIcon, ZapIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import {
  automationRouteId,
  itemLatestRunLabel,
  itemLocationLabel,
  itemName,
  itemProjectName,
  itemStatus,
  itemTriggerLabel,
  type AutomationListItem,
} from "./ForgeAutomationModel";

interface AutomationRowProps {
  readonly item: AutomationListItem;
  readonly projectNames: ReadonlyMap<string, string>;
  readonly busy: boolean;
  readonly onToggle: () => void;
  readonly onRunNow: () => void;
}

function AutomationRow({ item, projectNames, busy, onToggle, onRunNow }: AutomationRowProps) {
  const active = itemStatus(item) === "active";
  const LocationIcon = item.kind === "cloud" ? CloudIcon : LaptopIcon;
  return (
    <div className="grid min-h-touch grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-border/60 py-2 md:min-h-0 md:py-1.5">
      <Link
        className="min-w-0 rounded-md px-2 py-2 outline-none hover:bg-accent/45 focus-visible:ring-2 focus-visible:ring-ring md:py-1.5"
        to="/automations/$automationId"
        params={{ automationId: automationRouteId(item) }}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span
            className={cn(
              "size-2 shrink-0 rounded-full",
              active ? "bg-hue-sky" : "bg-muted-foreground/40",
            )}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground">
              {itemName(item)}
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-sm text-muted-foreground md:text-xs">
              <LocationIcon className="size-3.5 shrink-0" />
              <span className="truncate">{itemLocationLabel(item)}</span>
              <span aria-hidden>·</span>
              <span className="truncate">{itemProjectName(item, projectNames)}</span>
            </span>
          </span>
          <span className="hidden shrink-0 text-right text-xs text-muted-foreground sm:block">
            <span className="block">{itemTriggerLabel(item)}</span>
            <span className="mt-0.5 block">{itemLatestRunLabel(item)}</span>
          </span>
        </span>
      </Link>
      <span className="flex items-center gap-2">
        <Button
          aria-label="Run automation now"
          className={RESPONSIVE_ICON_BUTTON_CLASS}
          disabled={busy || !active}
          size="icon"
          variant="ghost"
          onClick={onRunNow}
        >
          <ZapIcon className={cn(busy ? "motion-safe:animate-status-pulse" : null)} />
        </Button>
        <Button
          aria-label={active ? "Pause automation" : "Resume automation"}
          className={RESPONSIVE_ICON_BUTTON_CLASS}
          disabled={busy}
          size="icon"
          variant="ghost"
          onClick={onToggle}
        >
          {active ? <PauseIcon /> : <PlayIcon />}
        </Button>
      </span>
    </div>
  );
}

interface AutomationGroupProps {
  readonly title: string;
  readonly items: readonly AutomationListItem[];
  readonly projectNames: ReadonlyMap<string, string>;
  readonly updatingAutomationKey: string | null;
  readonly onToggle: (item: AutomationListItem) => void;
  readonly onRunNow: (item: AutomationListItem) => void;
}

function AutomationGroup(props: AutomationGroupProps) {
  if (props.items.length === 0) return null;
  return (
    <section className="mt-8">
      <h2 className="text-sm font-medium text-foreground">{props.title}</h2>
      <div className="mt-2 border-t border-border/60">
        {props.items.map((item) => (
          <AutomationRow
            key={item.key}
            item={item}
            projectNames={props.projectNames}
            busy={props.updatingAutomationKey === item.key}
            onRunNow={() => props.onRunNow(item)}
            onToggle={() => props.onToggle(item)}
          />
        ))}
      </div>
    </section>
  );
}

export interface ForgeAutomationsListProps {
  readonly items: readonly AutomationListItem[];
  readonly projectNames: ReadonlyMap<string, string>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly updatingAutomationKey: string | null;
  readonly onToggle: (item: AutomationListItem) => void;
  readonly onRunNow: (item: AutomationListItem) => void;
}

export function ForgeAutomationsList(props: ForgeAutomationsListProps) {
  if (props.error && props.items.length === 0) {
    return <p className="mt-8 text-sm text-destructive">{props.error}</p>;
  }
  if (props.loading) {
    return (
      <div className="mt-8 space-y-2">
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
      </div>
    );
  }
  if (props.items.length === 0) {
    return (
      <div className="mt-10 border-y border-border/60 py-10 text-center">
        <h2 className="text-sm font-medium">No automations yet</h2>
        <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
          Run a reusable prompt on a schedule or whenever you need it.
        </p>
        <Button
          className={cn(RESPONSIVE_TEXT_BUTTON_CLASS, "mt-4")}
          render={<Link to="/automations/new" />}
          size="sm"
        >
          <PlusIcon />
          Create automation
        </Button>
      </div>
    );
  }
  const current = props.items.filter((item) => itemStatus(item) === "active");
  const paused = props.items.filter((item) => itemStatus(item) === "paused");
  return (
    <>
      {props.error ? (
        <p className="mt-6 text-sm text-destructive" role="alert">
          {props.error} Showing available automations.
        </p>
      ) : null}
      <AutomationGroup {...props} title="Active" items={current} />
      <AutomationGroup {...props} title="Paused" items={paused} />
    </>
  );
}
